import { FormBuilder, FormRef, ValidationResult } from '@wiberg/formbuilder';
import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { PFDialog } from '../../../../../Components/Common/PFDialog';
import { PFErrorBoundary } from '../../../../../Components/Common/PFErrorBoundary';
import { PFSpinner } from '../../../../../Components/Common/Spinner';
import { string } from '../../../../../forms/helpers/string';
import { guid } from '../../../../../helpers/guid';
import { Plugin } from '../../../../../Services/PluginInvoker';
import { PluginIDs } from '../../../../../Utilities/PluginIDs';
import { IMoveResourceActivitiesInfo } from '../../ResourceOverviewExtra';
import { moveAllActivitiesSchema } from './moveAllActivitiesSchema';
import { ApiCalls } from '../../../../../api/apiv2';
import { useTranslation } from '../../../../../Locales/i18n/localizationService';

interface IProps {
	info: IMoveResourceActivitiesInfo;
	clear: () => void;
}

export interface IMoveAllActivitiesDto {
	sourceResourceId: string;
	targetResourceId: string;
	type: "request" | "allocation" | "both";
}

export const MoveAllActivitiesDialog: FunctionComponent<IProps> = ({ info, clear }) => {
	const { t } = useTranslation();
	const formRef = useRef<FormRef<IMoveAllActivitiesDto>>();
	const [loading, setLoading] = useState(false);
	const [dto, setDto] = useState<IMoveAllActivitiesDto>();
	const [success, setSuccess] = useState<boolean>();
	const [showDialog, setShowDialog] = useState(false);

	useEffect(() => {
		const clone = {} as IMoveAllActivitiesDto;
		clone.sourceResourceId = info?.resourceId;
		clone.type = info?.type;
		setDto(clone);
		if (guid.isGuidNotEmpty(info?.resourceId))
			setShowDialog(true);
	}, [info?.resourceId, info?.type])

	const moveAllActivities = async () : Promise<void> => {
		
		const validationResult = await formRef.current.validateItem();
		if (validationResult === ValidationResult.Failed) return;
		
		setLoading(true);

		try {
			// requests are not ready in backend yet #gofundme (ewi)
			if (info?.type === 'allocation') await Plugin.InvokeNoResponse(PluginIDs.MoveAllAllocations, dto, t("MoveAllAllocationsDialog.FailedMovingAllocations"));
			if (info?.type === 'request') await ApiCalls.moveAllResourceRequests(dto);
		} catch(error) {
			setSuccess(false);
			setLoading(false);
			console.log(error);
			return;
		}

		setSuccess(true);
		setLoading(false);
	}

	const onChange = (item: IMoveAllActivitiesDto, prop: string, value: any) => {
		setDto(item);
	}

	const dismiss = () => {
		setDto(null);
		setShowDialog(false);
		setSuccess(null);
		clear();
	}

	const subText = (): string => {
		if (loading) return string.empty;
		if (success == null) return info?.type === 'allocation' ? t("MoveAllAllocationsDialog.SubText") : t("MoveAllResourceRequestsDialog.SubText");
		if (success) return info?.type === 'allocation' ? t("MoveAllAllocationsDialog.SubTextSuccess") : t("MoveAllResourceRequestsDialog.SubTextSuccess;")
		if (!success) return info?.type === 'allocation' ? t("MoveAllAllocationsDialog.FailedMovingAllocations") : t("MoveAllResourceRequestsDialog.FailedMovingRequests");
	}

	return <PFDialog
				title={info?.type === 'allocation' ? t("MoveAllAllocationsDialog.DialogTitle") : t("MoveAllResourceRequestsDialog.DialogTitle")}
				subText={ subText() }
				showDialog={ showDialog }
				maxWidth={600}
				callback={ success ? dismiss : moveAllActivities }
				dismissCallback={ dismiss }
				okButtonEnabled={!loading}
			>
				<div className="tp-moverequest-dialog-content">
					{ loading
						? 	<PFSpinner CustomClass="justoverridedefault" />
						: !success && <PFErrorBoundary ComponentName={info?.type === 'allocation' ? "move allocations dialog" : "move requests dialog"} Reset={ async () => setDto(null) }>
								<FormBuilder
									item={dto}
									schemaConfig={{ schemaProvider: {getSchema: async () => await moveAllActivitiesSchema.getSchema(info?.type), key: 'moveallactivitiesdialogfor'+info?.type} }}
									singleItemProps={{ onPropertyChange: onChange }}
									formRef={formRef}
								/>
							</PFErrorBoundary>
					}
				</div>
			</PFDialog>
}
